"use client";

/**
 * SmbPlansDropdown - the "Small Business" pricing section that sits UNDERNEATH
 * the three main pricing cards (Business / Enterprise / Corporate) on the
 * marketing pricing section. Always visible: the original collapsible
 * disclosure was removed 2026-06-10 (Kurt: "it should just be visible there").
 *
 * Approved SMB plans (Pricing v3, 2026-06-11; setup fee dropped 2026-07):
 *   Starter      50 included min  · 1 language · incoming calls only
 *   Standard     150 included min · (Most popular) · optional two-way add-on
 *   High-Volume  300 included min
 *   No setup fee on any SMB plan. Annual billing available at 10% off.
 *
 * TWO-CURRENCY: prices, overage rates, yearly totals, and checkout links come
 * from src/lib/constants/pricing-display.ts in the currency chosen by the
 * on-page US$ / CA$ toggle (passed down as `currency`). CAD checkout links are
 * env-driven and currently absent, so CAD CTAs gracefully fall back to a
 * contact page. Entitlement numbers (included minutes, language caps, the
 * two-way add-on price) come from src/lib/constants/plans.ts. Do not hardcode
 * dollar amounts here.
 *
 * Every SMB plan allows up to 1 voice agent (2 on Standard with the two-way
 * add-on). Starter is capped at 1 language; Standard and High-Volume at 2.
 *
 * Styling matches the host repo's Pricing component: inline React style objects
 * driven by global CSS custom properties (var(--color-*), var(--font-*)) plus
 * the named global classes `pricing-card` and `btn-primary` from globals.css.
 *
 * NOTE: we intentionally do NOT add the `featured` class to the highlighted
 * "Standard" card. `.pricing-card.featured::after` in globals.css hard-codes a
 * "Most Popular" CSS badge; reusing it here would double-render the badge. The
 * "Most popular" pill on the Standard card is rendered explicitly below.
 */

import { motion } from "framer-motion";
import { Check } from "lucide-react";
import {
  contactHref,
  contactLabel,
  formatMoney,
  formatRate,
  getPlanPricing,
  resolveCheckoutUrl,
  YEARLY_DISCOUNT_PCT,
  type BillingInterval,
  type Currency,
  type SmbPlanKey,
} from "@/lib/constants/pricing-display";
import { PLANS, PLAN_SLUGS } from "@/lib/constants/plans";

/** Re-exported for existing importers (Pricing.tsx, webhook docs). */
export type { SmbPlanKey } from "@/lib/constants/pricing-display";

export interface SmbPlan {
  key: SmbPlanKey;
  name: string;
  includedMinutes: number;
  /** Language cap for the plan (Starter: 1, others: 2). */
  maxLanguages: number;
  /** True when the plan handles incoming calls only (Starter). */
  inboundOnly?: boolean;
  /** Marketing one-liner shown under the price. */
  blurb: string;
  /** Whether to render this card as the visually highlighted ("most popular") plan. */
  featured?: boolean;
}

/**
 * The three approved SMB plans. Entitlements come from PLANS; monetary fields
 * (price, overage, checkout links) are resolved per-currency at render time.
 */
export const SMB_PLANS: readonly SmbPlan[] = [
  {
    key: "starter",
    name: "Starter",
    includedMinutes: PLANS[PLAN_SLUGS.STARTER].includedMinutes,
    maxLanguages: PLANS[PLAN_SLUGS.STARTER].maxLanguages,
    inboundOnly: true,
    blurb: "For small teams getting started with voice AI.",
  },
  {
    key: "standard",
    name: "Standard",
    includedMinutes: PLANS[PLAN_SLUGS.STANDARD].includedMinutes,
    maxLanguages: PLANS[PLAN_SLUGS.STANDARD].maxLanguages,
    blurb: "For growing teams that need more conversations.",
    featured: true,
  },
  {
    key: "high-volume",
    name: "High-Volume",
    includedMinutes: PLANS[PLAN_SLUGS.HIGH_VOLUME].includedMinutes,
    maxLanguages: PLANS[PLAN_SLUGS.HIGH_VOLUME].maxLanguages,
    blurb: "For busy teams with steady call volume.",
  },
];

/** The Standard two-way add-on monthly price, per currency (entitlements/plans.ts USD; CAD approved). */
const TWO_WAY_ADDON: Record<Currency, number> = {
  USD: PLANS[PLAN_SLUGS.STANDARD].twoWayAddOn?.priceUSD ?? 60,
  CAD: 100,
};

function planFeatures(plan: SmbPlan, currency: Currency): string[] {
  const overage = getPlanPricing(currency, plan.key).overage;
  const features = [
    "No setup fee",
    `Includes ${plan.includedMinutes.toLocaleString(
      currency === "CAD" ? "en-CA" : "en-US"
    )} minutes per month`,
    `${formatRate(overage)} ${currency} per additional minute`,
    "Up to 1 voice agent",
    `Up to ${plan.maxLanguages} language${plan.maxLanguages === 1 ? "" : "s"}`,
  ];
  if (plan.inboundOnly) {
    features.push("Incoming calls only");
  }
  if (plan.key === "standard") {
    features.push(
      `Optional two-way calling (inbound + outbound dialing): +${formatMoney(
        TWO_WAY_ADDON[currency],
        currency
      )} ${currency}/mo, up to 2 agents`
    );
  }
  return features;
}

export interface SmbPlansDropdownProps {
  /** Billing interval selected by the host's Monthly / Yearly toggle. */
  interval?: BillingInterval;
  /** Currency selected by the host's US$ / CA$ toggle. */
  currency?: Currency;
  /** Optional id for the section wrapper (anchor / deep-link support). */
  id?: string;
}

function PlanCard({
  plan,
  interval,
  currency,
}: {
  plan: SmbPlan;
  interval: BillingInterval;
  currency: Currency;
}) {
  const display = getPlanPricing(currency, plan.key);
  // Resolve the live checkout link for this currency+interval; null -> contact
  // fallback. CAD links are env-driven (currently absent) and yearly links may
  // be empty, so the CTA never points a foreign-currency price at a USD link.
  const url = resolveCheckoutUrl(currency, plan.key, interval);
  const href = url ?? contactHref(currency);
  const isExternal = href.startsWith("http");
  const ctaLabel = url ? `Choose ${plan.name}` : contactLabel(currency);

  const shownPrice = interval === "yearly" ? display.yearlyPerMonth : display.price;
  const billingNote =
    interval === "yearly"
      ? `${formatMoney(display.yearlyTotal, currency)} ${currency} billed annually (save ${YEARLY_DISCOUNT_PCT}%)`
      : "Billed monthly";

  return (
    <div
      className="pricing-card"
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        // Highlight the featured plan without the `.featured` class (see file
        // header) so globals.css does not inject a second "Most Popular" badge.
        ...(plan.featured
          ? {
              borderColor: "var(--color-accent)",
              boxShadow:
                "0 8px 32px rgba(0, 0, 0, 0.08), 0 0 20px rgba(100, 180, 255, 0.45)",
            }
          : {}),
      }}
    >
      {plan.featured && (
        <span
          style={{
            alignSelf: "center",
            display: "inline-block",
            fontFamily: "var(--font-body)",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--color-accent)",
            marginBottom: 12,
          }}
        >
          Most popular
        </span>
      )}

      <h3
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "1.5rem",
          color: "var(--color-text)",
          marginBottom: 4,
        }}
      >
        {plan.name}
      </h3>
      <p
        style={{
          fontSize: "0.88rem",
          color: "var(--color-text-muted)",
          fontWeight: 300,
          lineHeight: 1.5,
          marginBottom: 20,
          minHeight: 40,
        }}
      >
        {plan.blurb}
      </p>

      {/* Price */}
      <div style={{ marginBottom: 8 }}>
        <span
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "2.8rem",
            color: "var(--color-text)",
            lineHeight: 1,
          }}
        >
          {formatMoney(shownPrice, currency)}
        </span>
        <span
          style={{
            fontSize: "0.88rem",
            color: "var(--color-text-muted)",
            fontWeight: 300,
            marginLeft: 6,
          }}
        >
          {currency} / month
        </span>
      </div>
      <p
        style={{
          fontSize: "0.75rem",
          color: "var(--color-text-muted)",
          fontWeight: 300,
          marginBottom: 24,
          paddingBottom: 24,
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        {billingNote}
      </p>

      {/* Features */}
      <ul style={{ listStyle: "none", padding: 0, marginBottom: 32, textAlign: "left" }}>
        {planFeatures(plan, currency).map((f) => (
          <li
            key={f}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 12,
              fontSize: "0.88rem",
              color: "var(--color-text)",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            <Check
              size={16}
              style={{ flexShrink: 0, marginTop: 3 }}
              color={plan.featured ? "var(--color-accent)" : "var(--color-text-muted)"}
              strokeWidth={2}
            />
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href={href}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="btn-primary"
        style={{
          marginTop: "auto",
          width: "100%",
          justifyContent: "center",
          background: plan.featured ? "var(--color-text)" : "transparent",
          color: plan.featured ? "#fff" : "var(--color-text)",
          border: plan.featured ? "none" : "1.5px solid var(--color-border)",
          padding: "16px 24px",
        }}
      >
        {ctaLabel}
      </a>
    </div>
  );
}

export default function SmbPlansDropdown({
  interval = "monthly",
  currency = "USD",
  id = "smb-plans",
}: SmbPlansDropdownProps) {
  return (
    <section id={id} aria-label="Small Business plans" style={{ marginBottom: 40 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          textAlign: "center",
          marginBottom: 32,
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "1.5rem",
            color: "var(--color-text)",
            margin: 0,
          }}
        >
          Small Business Plans
        </h3>
        <p
          style={{
            fontSize: "0.88rem",
            fontWeight: 300,
            color: "var(--color-text-muted)",
            margin: 0,
          }}
        >
          Right-sized voice AI for smaller teams. No setup fee.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.3 }}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 24,
        }}
      >
        {SMB_PLANS.map((plan) => (
          <PlanCard key={plan.key} plan={plan} interval={interval} currency={currency} />
        ))}
      </motion.div>
    </section>
  );
}
